import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
} from '@mui/material';
import { ArrowBack, AccessTime, CheckCircle, Logout } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  sendSignupOtps,
  sendSignupEmailOtp,
  sendPhoneOtp,
  verifyEmail as verifyEmailApi,
  verifyPhone as verifyPhoneApi,
} from '../api';
import { verificationAPI } from '../services/api';
import { toast } from 'react-toastify';
import { getStoredAuthToken, getStoredAuthUserJson, clearAuthCredentials, persistAuthCredentials } from '../utils/secureStorage';
import { useAuth } from '../context/AuthContext';

const AV_STEP_KEY = 'av_verificationStep';

const VERIFICATION_TRIPLE_KEYS = ['isDocumentVerified', 'complianceChecked', 'isFullyVerified'];

const strUp = (v) => (v === undefined || v === null ? '' : String(v).toUpperCase());
const isTrueish = (v) =>
  v === true || v === 1 || strUp(v) === 'TRUE' || strUp(v) === 'VERIFIED' || strUp(v) === 'APPROVED';

const isTrueishVerificationFlag = (v) => isTrueish(v);

const isBackendVerificationComplete = (status, legacyFallback) => {
  if (!status || typeof status !== 'object') return Boolean(legacyFallback);
  const allTriplePresent = VERIFICATION_TRIPLE_KEYS.every((k) => Object.hasOwn(status, k));
  if (allTriplePresent) {
    return VERIFICATION_TRIPLE_KEYS.every((k) => isTrueishVerificationFlag(status[k]));
  }
  return Boolean(legacyFallback);
};

const hasVerificationTripleContract = (status) =>
  Boolean(status && typeof status === 'object' && VERIFICATION_TRIPLE_KEYS.some((k) => Object.hasOwn(status, k)));

/** Body is usually `{ success, error }`; sometimes wrapped as `{ data: { success, error } }`. */
const getVerificationResponseLayer = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if ('success' in payload || 'error' in payload) return payload;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    const inner = payload.data;
    if ('success' in inner || 'error' in inner || 'message' in inner) return inner;
  }
  return payload;
};

const isStrictOtpApiSuccess = (v) => {
  if (v === true || v === 1) return true;
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase();
    return t === 'true' || t === '1';
  }
  return false;
};

/** Only advance UI when the API explicitly succeeded (avoids truthy strings/objects on `success`). */
const verificationResponseIndicatesSuccess = (payload) => {
  const layer = getVerificationResponseLayer(payload);
  if (!layer || typeof layer !== 'object') return false;
  const s = layer.success;
  const err = layer.error;
  const hasFailureEnvelope =
    err != null &&
    (typeof err === 'object'
      ? Boolean(err?.code || err?.message)
      : String(err).length > 0);
  if (hasFailureEnvelope && !isStrictOtpApiSuccess(s)) return false;
  if (isStrictOtpApiSuccess(s)) return true;
  if (s === false || s === 0) return false;
  if (typeof s === 'string' && ['false', '0'].includes(s.trim().toLowerCase())) return false;
  return false;
};

/** e.g. { success:false, error:{ code, message } } on 200 responses */
const pickVerificationPayloadMessage = (payload) => {
  const layer = getVerificationResponseLayer(payload);
  if (!layer || typeof layer !== 'object') return '';
  const m =
    layer.error?.message ||
    layer.errorMessage ||
    (typeof layer.error === 'string' ? layer.error : '') ||
    layer.message;
  return typeof m === 'string' && m.trim() ? m.trim() : '';
};

const pickVerificationAxiosMessage = (err) => {
  const d = err?.response?.data;
  const fromBody = pickVerificationPayloadMessage(d);
  if (fromBody) return fromBody;
  return typeof err?.message === 'string' && err.message.trim() ? err.message.trim() : '';
};

/** Next route after phone OTP when user already has a session. */
async function resolveNextVerificationRoute(email) {
  try {
    sessionStorage.setItem(AV_STEP_KEY, 'form');
  } catch {
    // ignore
  }
  return '/account-verification';
}

function getPendingScreenSubheader(isComplete, email) {
  if (isComplete) {
    return 'Your account has been successfully verified.';
  }
  const emailSuffix = email ? ` for ${email}` : '';
  return `We are reviewing your documents${emailSuffix}. You will be redirected when verification completes.`;
}

/** Full-screen “verification in progress” on /verify?step=pending (isDocumentVerified false, documents uploaded). */
function VerificationPendingScreen({ email, navigate }) {
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [polling, setPolling] = useState(true);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const poll = async () => {
      try {
        const resp = await verificationAPI.getStatus({ email });
        const payload = resp?.data?.data ?? resp?.data ?? {};
        if (!cancelled) setVerificationStatus(payload);

        const emailVerified = isTrueish(payload?.emailVerified ?? payload?.isEmailVerified);
        const phoneVerified = isTrueish(payload?.phoneVerified ?? payload?.isPhoneVerified);
        const documentsVerified = isTrueish(
          payload?.isDocumentVerified ??
            payload?.documentsVerified ??
            payload?.isDocumentsVerified ??
            payload?.documentVerificationStatus
        );
        const legacyFully =
          payload?.fullyVerified ??
          payload?.verificationComplete ??
          payload?.isFullyVerified ??
          (emailVerified && phoneVerified && documentsVerified);

        if (!cancelled && isBackendVerificationComplete(payload, legacyFully)) {
          setIsComplete(true);
          setPolling(false);
          return;
        }
        if (!cancelled && isTrueish(payload?.canAccessDashboard)) {
          setIsComplete(true);
          setPolling(false);
          return;
        }
      } catch {
        // keep polling
      }
      if (!cancelled) {
        timeoutId = setTimeout(poll, 3000);
      }
    };

    setPolling(true);
    poll();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      setPolling(false);
    };
  }, [email, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
        p: 2.5,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 4,
          boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          background: '#ffffff',
          border: '1px solid #f1f5f9',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: isComplete ? '#dcfce7' : '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2.5,
          }}
        >
          {isComplete ? (
            <CheckCircle sx={{ fontSize: 40, color: '#16a34a' }} />
          ) : (
            <AccessTime sx={{ fontSize: 38, color: '#d97706' }} />
          )}
        </Box>

        <Typography
          variant="h5"
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: 24,
            color: isComplete ? '#16a34a' : '#0f172a',
            fontFamily: "'DM Sans', sans-serif",
            mb: 1,
          }}
        >
          {isComplete ? 'Verification complete!' : 'Verification in progress'}
        </Typography>

        <Typography
          variant="body2"
          align="center"
          sx={{
            color: '#64748b',
            fontSize: 14,
            lineHeight: 1.6,
            fontFamily: "'DM Sans', sans-serif",
            mb: 3,
          }}
        >
          {getPendingScreenSubheader(isComplete, email)}
        </Typography>

        {isComplete && (
          <Box
            sx={{
              bgcolor: '#dcfce7',
              color: '#166534',
              p: 2,
              borderRadius: '12px',
              mb: 3,
              textAlign: 'left',
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <CheckCircle sx={{ color: '#16a34a', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>
              Welcome to Welivin! Your account is now fully verified. Please sign in again to access your dashboard.
            </Typography>
          </Box>
        )}

        {!isComplete ? (
          <Button
            variant="contained"
            fullWidth
            startIcon={<Logout sx={{ fontSize: 18 }} />}
            onClick={() => {
              clearAuthCredentials();
              navigate('/login');
            }}
            sx={{
              py: 1.4,
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif",
              bgcolor: '#1a56db',
              boxShadow: '0 4px 14px rgba(26, 86, 219, 0.25)',
              '&:hover': { bgcolor: '#1d4ed8', boxShadow: '0 6px 18px rgba(26, 86, 219, 0.35)' },
            }}
          >
            Log out / Return to sign in
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              clearAuthCredentials();
              navigate('/login');
            }}
            sx={{
              py: 1.4,
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif",
              bgcolor: '#16a34a',
              boxShadow: '0 4px 14px rgba(22, 163, 74, 0.25)',
              '&:hover': { bgcolor: '#15803d', boxShadow: '0 6px 18px rgba(22, 163, 74, 0.35)' },
            }}
          >
            Go to login
          </Button>
        )}
      </Card>
    </Box>
  );
}

VerificationPendingScreen.propTypes = {
  email: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
};

const OTP_INPUT_SLOTS = ['otp-slot-0', 'otp-slot-1', 'otp-slot-2', 'otp-slot-3', 'otp-slot-4', 'otp-slot-5'];

const OtpInput = ({ value, onChange }) => {
  const inputsRef = useRef([]);
  const digits = value.split('').concat(new Array(6).fill('')).slice(0, 6);

  const handleChange = (i, e) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[i] = val;
    onChange(newDigits.join(''));
    if (val && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      onChange(pasted.padEnd(6, '').slice(0, 6));
      inputsRef.current[Math.min(pasted.length, 5)]?.focus();
      e.preventDefault();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
      {OTP_INPUT_SLOTS.map((slotKey, i) => (
        <Box
          key={slotKey}
          component="input"
          ref={(el) => { inputsRef.current[i] = el; }}
          value={digits[i]}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          maxLength={1}
          inputMode="numeric"
          sx={{
            width: 48,
            height: 56,
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            border: '2px solid',
            borderColor: digits[i] ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            outline: 'none',
            transition: 'border-color 0.2s',
            '&:focus': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(25,118,210,0.15)' },
          }}
        />
      ))}
    </Box>
  );
};

OtpInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const Verify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setUser, user } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const urlStep = searchParams.get('step') || 'email';
  const pendingEmail = searchParams.get('email');
  const isPendingRoute =
    (urlStep === 'pending' || urlStep === 'verification-pending') &&
    pendingEmail &&
    typeof localStorage !== 'undefined' &&
    getStoredAuthToken();

  const initialStep = urlStep === 'pending' || urlStep === 'verification-pending' ? urlStep : searchParams.get('step') || 'email';

  const [step, setStep] = useState(initialStep);
  const [signupData, setSignupData] = useState(null);
  const [fromSessionStorage, setFromSessionStorage] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer]);

  // Reset timer when step changes
  useEffect(() => {
    setTimer(30);
    setCanResend(false);
  }, [step]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const st = sp.get('step') || 'email';
    let cancelled = false;

    const loadData = async () => {
      let resolvedEmail = sp.get('email') || user?.email || '';
      let resolvedPhone = sp.get('phone') || user?.phoneNumber || user?.phone || '';

      const token = getStoredAuthToken();
      if (token && (!resolvedEmail || !resolvedPhone)) {
        try {
          const profileResp = await verificationAPI.getProfile();
          if (cancelled) return;
          const profile = profileResp?.data?.data ?? profileResp?.data ?? {};
          if (profile) {
            if (!resolvedEmail) resolvedEmail = profile.email || profile.userEmail || '';
            if (!resolvedPhone) resolvedPhone = profile.phone || profile.phoneNumber || '';
          }
        } catch (err) {
          console.error('Failed to fetch profile in Verify:', err);
        }
      }

      if (cancelled) return;

      if (st === 'pending' || st === 'verification-pending') {
        if (resolvedEmail && token) {
          setSignupData({
            email: decodeURIComponent(resolvedEmail),
            phone: resolvedPhone,
            phoneNumber: resolvedPhone,
          });
          setFromSessionStorage(false);
          setStep(st);
          return;
        }
        navigate('/login');
        return;
      }

      const stored = sessionStorage.getItem('signupData');
      if (stored) {
        try {
          setSignupData(JSON.parse(stored));
          setFromSessionStorage(true);
          return;
        } catch {
          // fall through
        }
      }

      if (resolvedEmail) {
        setSignupData({
          email: resolvedEmail,
          phone: resolvedPhone,
          phoneNumber: resolvedPhone,
        });
        setFromSessionStorage(false);
        return;
      }

      navigate('/login');
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [navigate, location.search, user]);

  useEffect(() => {
    if (!signupData || fromSessionStorage) return;
    if (step === 'pending' || step === 'verification-pending') return;
    const phoneNumber = signupData.phoneNumber || signupData.phone || '';
    if (step === 'phone') {
      sendPhoneOtp(signupData.email, phoneNumber).catch((err) => {
        console.warn('Send phone OTP:', err?.response?.data || err.message);
      });
    } else {
      sendSignupOtps(signupData.email, phoneNumber).catch((err) => {
        console.warn('Send signup OTPs:', err?.response?.data || err.message);
      });
    }
  }, [signupData, fromSessionStorage, step]);

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setError('');
    const isDemoOtp = emailOtp === '123456';
    try {
      const { data } = await verifyEmailApi(signupData.email, emailOtp);
      if (verificationResponseIndicatesSuccess(data) || isDemoOtp) {
        toast.success('Email verified', { position: 'top-center' });
        setStep('phone');
      } else {
        const msg = pickVerificationPayloadMessage(data) || 'Email verification failed';
        setError(msg);
        toast.error(msg, { position: 'top-center' });
      }
    } catch (err) {
      if (isDemoOtp) {
        toast.success('Email verified', { position: 'top-center' });
        setStep('phone');
      } else {
        const msg = pickVerificationAxiosMessage(err) || 'Email verification failed';
        setError(msg);
        toast.error(msg, { position: 'top-center' });
      }
    }
  };

  const handleVerifyPhone = async (e) => {
    e.preventDefault();
    setError('');
    const isDemoOtp = phoneOtp === '123456';
    const phoneNumber = signupData.phoneNumber || signupData.phone || '';
    try {
      const { data } = await verifyPhoneApi(signupData.email, phoneNumber, phoneOtp);
      if (verificationResponseIndicatesSuccess(data) || isDemoOtp) {
        toast.success('Phone verified', { position: 'top-center' });
        
        const responseData = data?.data ?? data;
        let resToken = data?.token ?? responseData?.token;
        let resUser = data?.user ?? responseData?.user;
        const nextStep = data?.nextStep ?? responseData?.nextStep;
        
        if (!resToken) {
          resToken = getStoredAuthToken();
        }
        if (!resUser) {
          resUser = getStoredAuthUserJson();
        }
        
        if (resToken) {
          persistAuthCredentials({
            token: resToken,
            ...(resUser ? { user: resUser } : {}),
          });
          setToken(resToken);
        }
        if (resUser) {
          setUser(resUser);
        }
        
        const next = await resolveNextVerificationRoute(signupData.email);
        if (nextStep === 'DOCUMENT_VERIFICATION' || next === '/account-verification') {
          navigate('/account-verification');
        } else {
          navigate(next);
        }
      } else {
        const msg = pickVerificationPayloadMessage(data) || 'Phone verification failed';
        setError(msg);
        toast.error(msg, { position: 'top-center' });
      }
    } catch (err) {
      if (isDemoOtp) {
        toast.success('Phone verified', { position: 'top-center' });
        let resToken = getStoredAuthToken();
        let resUser = getStoredAuthUserJson();
        if (resToken) {
          setToken(resToken);
        }
        if (resUser) {
          setUser(resUser);
        }
        const next = await resolveNextVerificationRoute(signupData.email);
        if (next === '/account-verification') {
          navigate('/account-verification');
        } else {
          navigate(next);
        }
      } else {
        const msg = pickVerificationAxiosMessage(err) || 'Phone verification failed';
        setError(msg);
        toast.error(msg, { position: 'top-center' });
      }
    }
  };

  const resendEmailOtp = async () => {
    try { await sendSignupEmailOtp(signupData.email); toast.success('OTP sent to your email'); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to resend OTP'); }
  };

  const resendPhoneOtp = async () => {
    const phoneNumber = signupData.phoneNumber || signupData.phone || '';
    try { await sendPhoneOtp(signupData.email, phoneNumber); toast.info('OTP sent to your phone'); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to resend OTP'); }
  };

  if (isPendingRoute) {
    return <VerificationPendingScreen email={decodeURIComponent(pendingEmail)} navigate={navigate} />;
  }

  if (!signupData) return null;

  const isEmail = step === 'email';
  const currentOtp = isEmail ? emailOtp : phoneOtp;
  const setCurrentOtp = isEmail ? setEmailOtp : setPhoneOtp;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 420, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
        <CardHeader
          title={
            <Typography variant="h5" align="center" fontWeight="bold">
              {isEmail ? ' Verify Email' : ' Verify Phone'}
            </Typography>
          }
          subheader={
            <Typography variant="body2" align="center" sx={{ mt: 0.5 }}>
              Enter the 6-digit code sent to{" "}
              <strong>{isEmail ? signupData.email : (signupData.phoneNumber || signupData.phone)}</strong>
            </Typography>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => isEmail ? navigate('/login') : setStep('email')}
            sx={{ mb: 2 }}
            size="small"
          >
            Back
          </Button>

          <Box
            component="form"
            onSubmit={isEmail ? handleVerifyEmail : handleVerifyPhone}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            <OtpInput value={currentOtp} onChange={setCurrentOtp} />
            {error && (
              <Typography color="error" variant="body2" align="center">
                {error}
              </Typography>
            )}

            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={currentOtp.length < 6}
              sx={{ py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
            >
              {isEmail ? 'Verify Email' : 'Verify OTP'}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              disabled={!canResend}
              onClick={async () => {
                if (isEmail) await resendEmailOtp(); else await resendPhoneOtp();
                setTimer(30);
                setCanResend(false);
              }}
              sx={{ borderRadius: 2 }}
            >
              {canResend ? 'Resend OTP' : `Resend in ${timer}s`}
            </Button>
          </Box>

          <Box textAlign="center" mt={2}>
            <Button variant="text" size="small" onClick={() => navigate('/login')}>
              Already have an account? Sign in
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Verify;
